/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { useState, useRef, useEffect } from '@wordpress/element';

/**
 * Internal dependencies
 */
import BookingStatusBadge from './BookingStatusBadge';
import SortButton from './SortButton';

/**
 * Booking List Table component
 *
 * @param {Object}   props
 * @param {Array}    props.bookings        Array of bookings to display.
 * @param {Function} props.onDeleteBooking Function to handle booking deletion.
 * @param {Function} props.onEditBooking   Function to handle booking edit.
 * @param {Function} props.onStatusUpdate  Function to handle status updates.
 * @param {Function} props.onCreateNew     Function to handle creating a new booking.
 * @param {boolean}  props.isLoading       Whether the table is loading.
 * @param            props.sort
 * @param            props.onSortChange
 * @return {JSX.Element} The BookingListTable component.
 */
const BookingListTable = ( {
	bookings,
	onDeleteBooking,
	onEditBooking,
	onStatusUpdate,
	onCreateNew,
	isLoading = false,
	sort = null,
	onSortChange = () => {},
} ) => {
	const [ openMenuId, setOpenMenuId ] = useState( null );
	const [ copiedId, setCopiedId ] = useState( null );
	const menuRefs = useRef( {} );

	/**
	 * Handle clicking outside menu to close it
	 */
	useEffect( () => {
		const handleClickOutside = ( event ) => {
			if (
				openMenuId &&
				menuRefs.current[ openMenuId ] &&
				! menuRefs.current[ openMenuId ].contains( event.target )
			) {
				setOpenMenuId( null );
			}
		};

		document.addEventListener( 'mousedown', handleClickOutside );
		return () => {
			document.removeEventListener( 'mousedown', handleClickOutside );
		};
	}, [ openMenuId ] );

	/**
	 * Toggle menu visibility
	 *
	 * @param {number} bookingId The booking ID.
	 */
	const toggleMenu = ( bookingId ) => {
		setOpenMenuId( openMenuId === bookingId ? null : bookingId );
	};

	/**
	 * Close menu after action
	 */
	const closeMenu = () => {
		setOpenMenuId( null );
	};

	/**
	 * Copy booking ID to clipboard
	 *
	 * @param {string} bookingId The booking ID to copy.
	 */
	const copyBookingId = async ( bookingId ) => {
		try {
			// Try the modern Clipboard API first
			if ( navigator.clipboard && window.isSecureContext ) {
				await navigator.clipboard.writeText( bookingId );
				setCopiedId( bookingId );
				setTimeout( () => setCopiedId( null ), 2000 );
			} else {
				// Fallback: show the booking ID in a user-friendly way
				// Create a temporary input element for manual copy
				const tempInput = document.createElement( 'input' );
				tempInput.value = bookingId;
				tempInput.style.position = 'fixed';
				tempInput.style.left = '-999999px';
				tempInput.style.top = '-999999px';
				document.body.appendChild( tempInput );
				tempInput.focus();
				tempInput.select();

				// Show user a message that they need to copy manually
				alert(
					__(
						'Booking ID copied to clipboard. If not copied automatically, please copy manually:',
						'nemtly-booking'
					) + bookingId
				);

				document.body.removeChild( tempInput );
				setCopiedId( bookingId );
				setTimeout( () => setCopiedId( null ), 2000 );
			}
		} catch ( err ) {
			// Show user a message that they need to copy manually
			alert(
				__( 'Please copy the booking ID manually:', 'nemtly-booking' ) +
					bookingId
			);
		}
	};

	/**
	 * Format date for display - compact version
	 *
	 * @param {string} dateString The date string to format.
	 * @return {string} Formatted date string.
	 */
	const formatDate = ( dateString ) => {
		if ( ! dateString ) {
			return '-';
		}
		const date = new Date( dateString );
		return date.toLocaleDateString( 'en-US', {
			month: 'short',
			day: 'numeric',
			year: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
		} );
	};

	/**
	 * Format date for display - very compact version for created column
	 *
	 * @param {string} dateString The date string to format.
	 * @return {string} Formatted date string.
	 */
	const formatCreatedDate = ( dateString ) => {
		if ( ! dateString ) {
			return '-';
		}
		const date = new Date( dateString );
		return date.toLocaleDateString( 'en-US', {
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		} );
	};

	/**
	 * Get shortened booking ID for display
	 *
	 * @param {string} bookingId The full booking ID.
	 * @return {string} Shortened booking ID.
	 */
	const getShortenedBookingId = ( bookingId ) => {
		if ( ! bookingId ) {
			return '';
		}
		return bookingId.length > 8
			? `${ bookingId.substring( 0, 8 ) }...`
			: bookingId;
	};

	/**
	 * Handle status change
	 *
	 * @param {number} bookingId The booking ID.
	 * @param {string} newStatus The new status.
	 */
	const handleStatusChange = ( bookingId, newStatus ) => {
		onStatusUpdate( bookingId, newStatus );
		closeMenu();
	};

	/**
	 * Handle edit booking
	 *
	 * @param {number} bookingId The booking ID.
	 */
	const handleEditBooking = ( bookingId ) => {
		onEditBooking( bookingId );
		closeMenu();
	};

	/**
	 * Handle delete booking
	 *
	 * @param {number} bookingId The booking ID.
	 */
	const handleDeleteBooking = ( bookingId ) => {
		onDeleteBooking( bookingId );
		closeMenu();
	};

	/**
	 * Get available status options for a booking
	 *
	 * @param {Object} booking The booking object.
	 * @return {Array} Array of status options.
	 */
	const getStatusOptions = ( booking ) => {
		const currentStatus = booking.booking_status;
		const options = [
			{ label: __( 'Pending', 'nemtly-booking' ), value: 'pending' },
			{ label: __( 'Confirmed', 'nemtly-booking' ), value: 'confirmed' },
			{ label: __( 'Cancelled', 'nemtly-booking' ), value: 'cancelled' },
		];

		return options.filter( ( option ) => option.value !== currentStatus );
	};

	if ( ! bookings || bookings.length === 0 ) {
		return (
			<div className="nemtly-booking-empty-state">
				<h3>{ __( 'No bookings found', 'nemtly-booking' ) }</h3>
				<p>
					{ __(
						'No bookings match your current criteria.',
						'nemtly-booking'
					) }
				</p>
			</div>
		);
	}

	return (
		<div className="nb-relative nb-shadow sm:nb-rounded-lg nb-overflow-visible">
			{ isLoading && (
				<div className="nb-absolute nb-inset-0 nb-bg-white nb-bg-opacity-75 nb-flex nb-items-center nb-justify-center nb-z-10">
					<div className="nb-animate-spin nb-rounded-full nb-h-8 nb-w-8 nb-border-b-2 nb-border-indigo-600"></div>
				</div>
			) }

			<table className="nb-min-w-full nb-divide-y nb-divide-gray-300">
				<thead className="nb-bg-gray-50">
					<tr>
						<th
							scope="col"
							className="nb-py-3.5 nb-pl-4 nb-pr-3 nb-text-left nb-text-sm nb-font-semibold nb-text-gray-900 sm:nb-pl-6"
						>
							{ __( 'Booking ID', 'nemtly-booking' ) }
						</th>
						<th
							scope="col"
							className="nb-px-3 nb-py-3.5 nb-text-left"
						>
							<SortButton
								label={ __( 'Customer', 'nemtly-booking' ) }
								column="attendee_name"
								activeColumn={ sort?.orderby }
								direction={ sort?.order }
								defaultDirection="ASC"
								className="nb-text-sm nb-font-semibold"
								onSortChange={ onSortChange }
							/>
						</th>
						<th
							scope="col"
							className="nb-px-3 nb-py-3.5 nb-text-left nb-text-sm nb-font-semibold nb-text-gray-900"
						>
							{ __( 'Event', 'nemtly-booking' ) }
						</th>
						<th
							scope="col"
							className="nb-px-3 nb-py-3.5 nb-text-left"
						>
							<SortButton
								label={ __( 'Booking Date', 'nemtly-booking' ) }
								column="booking_date"
								activeColumn={ sort?.orderby }
								direction={ sort?.order }
								defaultDirection="ASC"
								className="nb-text-sm nb-font-semibold"
								onSortChange={ onSortChange }
							/>
						</th>
						<th
							scope="col"
							className="nb-px-3 nb-py-3.5 nb-text-left nb-text-sm nb-font-semibold nb-text-gray-900"
						>
							{ __( 'Status', 'nemtly-booking' ) }
						</th>
						<th
							scope="col"
							className="nb-px-3 nb-py-3.5 nb-text-left nb-hidden lg:nb-table-cell"
						>
							<SortButton
								label={ __( 'Created', 'nemtly-booking' ) }
								column="created_at"
								activeColumn={ sort?.orderby }
								direction={ sort?.order }
								defaultDirection="DESC"
								className="nb-text-sm nb-font-semibold"
								onSortChange={ onSortChange }
							/>
						</th>
						<th
							scope="col"
							className="nb-relative nb-py-3.5 nb-pl-3 nb-pr-4 sm:nb-pr-6"
						>
							<span className="nb-sr-only">
								{ __( 'Actions', 'nemtly-booking' ) }
							</span>
						</th>
					</tr>
				</thead>
				<tbody className="nb-divide-y nb-divide-gray-200 nb-bg-white">
					{ bookings.map( ( booking ) => (
						<tr key={ booking.id }>
							<td className="nb-whitespace-nowrap nb-py-4 nb-pl-4 nb-pr-3 nb-text-sm nb-font-medium sm:nb-pl-6">
								<div className="nb-flex nb-items-center nb-gap-2">
									<strong className="nb-text-gray-900">
										#
										{ getShortenedBookingId(
											booking.uuid
										) }
									</strong>
									<button
										type="button"
										onClick={ () =>
											copyBookingId( booking.uuid )
										}
										className="nb-inline-flex nb-items-center nb-justify-center nb-w-6 nb-h-6 nb-text-gray-400 hover:nb-text-gray-600 focus:nb-outline-none"
										title={
											copiedId === booking.uuid
												? __(
														'Copied!',
														'nemtly-booking'
												  )
												: __(
														'Copy full ID',
														'nemtly-booking'
												  )
										}
									>
										{ copiedId === booking.uuid ? (
											<svg
												className="nb-w-4 nb-h-4 nb-text-green-500"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={ 2 }
													d="M5 13l4 4L19 7"
												/>
											</svg>
										) : (
											<svg
												className="nb-w-4 nb-h-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={ 2 }
													d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
												/>
											</svg>
										) }
									</button>
								</div>
							</td>
							<td className="nb-whitespace-nowrap nb-px-3 nb-py-4 nb-text-sm nb-text-gray-500">
								<div className="nb-attendee-info">
									<strong className="nb-text-gray-900">
										{ booking.attendee_name }
									</strong>
									<br />
									<a
										href={ `mailto:${ booking.attendee_email }` }
										className="nb-text-indigo-600 hover:nb-text-indigo-900"
									>
										{ booking.attendee_email }
									</a>
									{ booking.attendee_phone && (
										<>
											<br />
											<small className="nb-text-gray-500">
												{ booking.attendee_phone }
											</small>
										</>
									) }
								</div>
							</td>
							<td className="nb-whitespace-nowrap nb-px-3 nb-py-4 nb-text-sm nb-text-gray-500">
								<div className="nb-event-info">
									{ booking.event && booking.event.title ? (
										<a
											href={ `/wp-admin/admin.php?page=nemtly-booking#/events/${ booking.event.id }` }
											className="nb-text-indigo-600 hover:nb-text-indigo-900"
										>
											{ booking.event.title }
										</a>
									) : (
										<span className="nb-text-gray-400">
											{ __(
												'Event not found',
												'nemtly-booking'
											) }
										</span>
									) }
								</div>
							</td>
							<td className="nb-whitespace-nowrap nb-px-3 nb-py-4 nb-text-sm nb-text-gray-500">
								<div className="nb-booking-date">
									{ formatDate( booking.booking_date ) }
								</div>
							</td>
							<td className="nb-whitespace-nowrap nb-px-3 nb-py-4 nb-text-sm nb-text-gray-500">
								<BookingStatusBadge
									status={ booking.booking_status }
								/>
							</td>
							<td className="nb-whitespace-nowrap nb-px-3 nb-py-4 nb-text-sm nb-text-gray-500 nb-hidden lg:nb-table-cell">
								{ formatCreatedDate( booking.created_at ) }
							</td>
							<td className="nb-relative nb-whitespace-nowrap nb-py-4 nb-pl-3 nb-pr-4 nb-text-right nb-text-sm nb-font-medium sm:nb-pr-6">
								<div
									className="nb-relative nb-inline-block nb-text-left"
									ref={ ( el ) =>
										( menuRefs.current[ booking.id ] = el )
									}
								>
									<button
										type="button"
										className="nb-inline-flex nb-items-center nb-justify-center nb-w-8 nb-h-8 nb-bg-white nb-border nb-border-gray-300 nb-rounded-full nb-shadow-sm hover:nb-bg-gray-50 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-offset-2 focus:nb-ring-indigo-500"
										onClick={ () =>
											toggleMenu( booking.id )
										}
										aria-expanded={
											openMenuId === booking.id
										}
										aria-haspopup="true"
									>
										<span className="nb-sr-only">
											{ __(
												'Open options',
												'nemtly-booking'
											) }
										</span>
										<svg
											className="nb-w-5 nb-h-5 nb-text-gray-400"
											viewBox="0 0 20 20"
											fill="currentColor"
											aria-hidden="true"
										>
											<path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 8.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM11.5 15.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z" />
										</svg>
									</button>

									{ openMenuId === booking.id && (
										<div className="nb-absolute nb-right-0 nb-z-50 nb-mt-2 nb-w-48 nb-origin-top-right nb-rounded-md nb-bg-white nb-py-1 nb-shadow-lg nb-ring-1 nb-ring-black nb-ring-opacity-5 focus:nb-outline-none">
											<button
												type="button"
												className="nb-block nb-w-full nb-px-4 nb-py-2 nb-text-left nb-text-sm nb-text-gray-700 hover:nb-bg-gray-100"
												onClick={ () =>
													handleEditBooking(
														booking.id
													)
												}
											>
												{ __(
													'Edit',
													'nemtly-booking'
												) }
											</button>

											{ booking.payment && (
												<a
													href="admin.php?page=nemtly-booking-payments"
													className="nb-block nb-w-full nb-px-4 nb-py-2 nb-text-left nb-text-sm nb-text-gray-700 hover:nb-bg-gray-100 nb-no-underline"
													onClick={ closeMenu }
												>
													{ __(
														'View payment',
														'nemtly-booking'
													) }
												</a>
											) }

											{ booking.booking_status ===
												'pending' && (
												<button
													type="button"
													className="nb-block nb-w-full nb-px-4 nb-py-2 nb-text-left nb-text-sm nb-text-green-700 hover:nb-bg-gray-100"
													onClick={ () =>
														handleStatusChange(
															booking.id,
															'confirmed'
														)
													}
												>
													{ __(
														'Confirm',
														'nemtly-booking'
													) }
												</button>
											) }

											{ booking.booking_status !==
												'cancelled' && (
												<button
													type="button"
													className="nb-block nb-w-full nb-px-4 nb-py-2 nb-text-left nb-text-sm nb-text-yellow-700 hover:nb-bg-gray-100"
													onClick={ () =>
														handleStatusChange(
															booking.id,
															'cancelled'
														)
													}
												>
													{ __(
														'Cancel',
														'nemtly-booking'
													) }
												</button>
											) }

											<button
												type="button"
												className="nb-block nb-w-full nb-px-4 nb-py-2 nb-text-left nb-text-sm nb-text-red-700 hover:nb-bg-gray-100"
												onClick={ () =>
													handleDeleteBooking(
														booking.id
													)
												}
											>
												{ __(
													'Delete',
													'nemtly-booking'
												) }
											</button>
										</div>
									) }
								</div>
							</td>
						</tr>
					) ) }
				</tbody>
			</table>
		</div>
	);
};

export default BookingListTable;
